export type CollaborationRegion = {
  id: string;
  name: string;
  countries: readonly string[];
};

export type CollaborationRegionsData = {
  regions: readonly CollaborationRegion[];
};

export const collaborationRegionsData: CollaborationRegionsData = {
  regions: [
    {
      id: "indian-subcontinent",
      name: "Indian Subcontinent",
      countries: ["India", "Bangladesh", "Nepal", "Sri Lanka", "Bhutan"],
    },
    {
      id: "asean-mainland",
      name: "ASEAN – Mainland Southeast Asia",
      countries: ["Thailand", "Vietnam", "Cambodia", "Laos", "Myanmar"],
    },
    {
      id: "asean-maritime",
      name: "ASEAN – Maritime Southeast Asia",
      countries: ["Indonesia", "Malaysia", "Singapore", "Philippines"],
    },
    {
      id: "east-asia",
      name: "East Asia",
      countries: ["Japan", "South Korea"],
    },
    {
      id: "greater-china",
      name: "Greater China",
      countries: ["China", "Taiwan", "Hong Kong"],
    },
    {
      id: "middle-east-gcc",
      name: "Middle East – GCC",
      countries: ["Saudi Arabia", "United Arab Emirates (UAE)", "Qatar", "Kuwait", "Oman", "Bahrain"],
    },
    {
      id: "middle-east-levant",
      name: "Middle East – Levant",
      countries: ["Israel", "Jordan", "Lebanon"],
    },
    {
      id: "middle-east-north-africa",
      name: "Middle East & North Africa (Non-GCC)",
      countries: ["Egypt", "Turkey", "Morocco"],
    },
    {
      id: "north-america",
      name: "North America",
      countries: ["USA", "Canada", "Mexico"],
    },
    {
      id: "latin-america-africa",
      name: "Latin America & Africa",
      countries: ["Brazil", "South Africa", "Kenya"],
    },
    {
      id: "northern-europe",
      name: "Northern Europe",
      countries: ["United Kingdom", "Ireland", "Sweden", "Denmark", "Finland", "Norway", "Iceland"],
    },
    {
      id: "western-europe",
      name: "Western Europe",
      countries: ["Germany", "France", "Netherlands", "Belgium", "Luxembourg", "Switzerland", "Austria"],
    },
    {
      id: "eastern-europe",
      name: "Eastern Europe",
      countries: [
        "Poland",
        "Czech Republic",
        "Slovakia",
        "Hungary",
        "Romania",
        "Bulgaria",
        "Lithuania",
        "Latvia",
        "Estonia",
        "Ukraine",
      ],
    },
    {
      id: "southern-europe",
      name: "Southern Europe",
      countries: ["Italy", "Spain", "Portugal", "Greece", "Malta", "Cyprus", "Croatia", "Slovenia"],
    },
    {
      id: "oceania",
      name: "Oceania",
      countries: ["Australia", "New Zealand"],
    },
  ],
};

export function getCollaborationRegionById(regionId: string): CollaborationRegion | undefined {
  return collaborationRegionsData.regions.find((r) => r.id === regionId);
}

function normalizeRegionLookupKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/\s+/g, " ");
}

/** Resolve a stored region id, display name, or legacy label to a catalog region id. */
export function resolveCollaborationRegionId(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;

  if (getCollaborationRegionById(trimmed)) return trimmed;

  const key = normalizeRegionLookupKey(trimmed);
  for (const region of collaborationRegionsData.regions) {
    if (normalizeRegionLookupKey(region.id) === key) return region.id;
    if (normalizeRegionLookupKey(region.name) === key) return region.id;
  }

  return null;
}

/** Normalize profile/UI region values to catalog region ids (deduped, order preserved). */
export function resolveCollaborationRegionIds(values: readonly string[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const value of values) {
    const id = resolveCollaborationRegionId(value);
    if (!id || seen.has(id)) continue;
    seen.add(id);
    ids.push(id);
  }
  return ids;
}

export function getCollaborationRegionByCountry(countryName: string): CollaborationRegion | null {
  const normalized = countryName.trim();
  if (!normalized) return null;
  for (const region of collaborationRegionsData.regions) {
    if (region.countries.includes(normalized)) return region;
  }
  return null;
}

export function getCollaborationCountriesForRegions(regionIds: readonly string[]): string[] {
  const countries = new Set<string>();
  for (const regionId of resolveCollaborationRegionIds(regionIds)) {
    const region = getCollaborationRegionById(regionId);
    if (!region) continue;
    for (const country of region.countries) countries.add(country);
  }
  return [...countries].sort((a, b) => a.localeCompare(b));
}

export function geoCountryOptionId(countryName: string): string {
  return `geo_${countryName
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")}`;
}

export function resolveGeoCountryLabel(
  optionId: string,
  catalog: CollaborationRegionsData = collaborationRegionsData,
): string | null {
  for (const region of catalog.regions) {
    for (const country of region.countries) {
      if (geoCountryOptionId(country) === optionId) return country;
    }
  }
  return null;
}

export function deriveCollaborationRegionIdsFromCountries(countries: readonly string[]): string[] {
  const regionIds = new Set<string>();
  for (const country of countries) {
    const region = getCollaborationRegionByCountry(country);
    if (region) regionIds.add(region.id);
  }
  return [...regionIds];
}

export function getAllCollaborationTargetCountries(): string[] {
  return collaborationRegionsData.regions.flatMap((r) => [...r.countries]);
}
